import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, getToken, setToken, TOKEN_KEY } from '../api/client.js';

const AuthContext = createContext(null);

// Sign out after this long without any interaction, so an account left open on
// a shared or borrowed phone does not stay open. Two minutes is deliberately
// short: these accounts hold rent balances and tenants' details, and the phone
// this runs on is often not the owner's alone.
export const IDLE_LIMIT_MS = 2 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'ndaki.last-activity';
export const IDLE_SIGNOUT_FLAG = 'ndaki.idle-signout';

// `initialUser` lets a caller hand in an already-known session instead of waiting
// for /auth/me. The browser entry never passes it; the render smoke test does, so
// the signed-in pages can be exercised without a live API.
export const AuthProvider = ({ children, initialUser = null }) => {
  const [user, setUser] = useState(initialUser);
  const [managingFor, setManagingFor] = useState(null);
  const [counts, setCounts] = useState({ notifications: 0, messages: 0 });
  const [loading, setLoading] = useState(!initialUser && Boolean(getToken()));

  const loadSession = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const data = await api.get('/auth/me');
      setUser(data.user);
      setManagingFor(data.managingFor || null);
      setCounts({ notifications: data.unreadNotifications, messages: data.unreadMessages });
    } catch {
      // Bad or expired token. The client already cleared it.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // The API client fires this when a request comes back 401.
  useEffect(() => {
    const onSignedOut = () => setUser(null);
    window.addEventListener('ndaki:signed-out', onSignedOut);
    return () => window.removeEventListener('ndaki:signed-out', onSignedOut);
  }, []);

  // Badge counts go stale as soon as somebody else acts, so refresh them while
  // the tab is in the foreground.
  //
  // The same call already carries the account itself, so the account is taken
  // from it too. An administrator correcting somebody's role used to leave that
  // person's open tab believing the old one - landlord navigation, landlord
  // pages, and their own lease nowhere to be found - until they signed out and
  // back in. Now the tab follows the change on its own within the minute.
  useEffect(() => {
    if (!user) return undefined;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      api
        .get('/auth/me')
        .then((data) => {
          setCounts({ notifications: data.unreadNotifications, messages: data.unreadMessages });
          setUser((current) => {
            if (!current || !data.user) return current;
            // Replace only on a real change, so the rest of the app is not
            // re-rendered every minute for nothing.
            return JSON.stringify(current) === JSON.stringify(data.user) ? current : data.user;
          });
          setManagingFor(data.managingFor ?? null);
        })
        .catch(() => {});
    };
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [user]);

  const signIn = useCallback(async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    if (data.mfaRequired) return { mfaRequired: true, mfaToken: data.mfaToken, email: data.email };
    setToken(data.token);
    setUser(data.user);
    await loadSession();
    return data.user;
  }, [loadSession]);

  // Completes an admin login after /login returned mfaRequired: the code the
  // admin was emailed, checked against the mfaToken from that first step.
  const verifyMfa = useCallback(async (mfaToken, code) => {
    const data = await api.post('/auth/verify-mfa', { mfaToken, code });
    setToken(data.token);
    setUser(data.user);
    await loadSession();
    return data.user;
  }, [loadSession]);

  const resendMfa = useCallback(async (mfaToken) => {
    const data = await api.post('/auth/resend-mfa', { mfaToken });
    return data.mfaToken;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await api.post('/auth/register', payload);
    setToken(data.token);
    setUser(data.user);
    await loadSession();
    return data.user;
  }, [loadSession]);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    setManagingFor(null);
    setCounts({ notifications: 0, messages: 0 });
  }, []);

  // The inactivity clock. The timestamp lives in localStorage so that working
  // in one tab keeps a second tab alive - both share the token, so an idle tab
  // timing out alone would pull the session out from under the active one.
  useEffect(() => {
    if (!user) return undefined;

    let lastStamp = 0;
    const stamp = () => {
      const now = Date.now();
      // Mouse moves arrive in floods; one write every few seconds is plenty.
      if (now - lastStamp < 5_000) return;
      lastStamp = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };
    stamp();

    const check = () => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || Date.now();
      if (Date.now() - last >= IDLE_LIMIT_MS) {
        // The login page explains why the session ended.
        sessionStorage.setItem(IDLE_SIGNOUT_FLAG, '1');
        signOut();
      }
    };

    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];
    events.forEach((name) => window.addEventListener(name, stamp, { passive: true }));
    // Catch a tab that was left in the background past the limit the moment it
    // comes back, rather than on the next sweep.
    document.addEventListener('visibilitychange', check);
    // Checked often enough that the sign-out lands close to the two minutes
    // promised, instead of up to fifteen seconds after it.
    const timer = setInterval(check, 5_000);

    return () => {
      events.forEach((name) => window.removeEventListener(name, stamp));
      document.removeEventListener('visibilitychange', check);
      clearInterval(timer);
    };
  }, [user, signOut]);

  // When another tab signs out (idle timeout or by hand), the token vanishes
  // from localStorage; follow it here instead of waiting for a 401.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === TOKEN_KEY && !event.newValue) {
        setUser(null);
        setCounts({ notifications: 0, messages: 0 });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const assumeSession = useCallback(async (token, assumedUser) => {
    setToken(token);
    setUser(assumedUser || null);
    await loadSession();
  }, [loadSession]);

  const value = useMemo(
    () => ({
      user,
      managingFor,
      counts,
      setCounts,
      loading,
      signIn,
      verifyMfa,
      resendMfa,
      register,
      signOut,
      assumeSession,
      refresh: loadSession,
      updateUser: (patch) => setUser((current) => ({ ...current, ...patch })),
      isTenant: user?.role === 'tenant',
      isLandlord: user?.role === 'landlord',
      isManager: user?.role === 'manager',
      isAdmin: user?.role === 'admin'
    }),
    [user, managingFor, counts, loading, signIn, verifyMfa, resendMfa, register, signOut, assumeSession, loadSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
};

// Where each role lands after signing in. Takes a role or the whole user: a
// manager narrowed to the meters has no business on the landlord dashboard,
// which would only fill with panels the API refuses them.
export const homeFor = (roleOrUser) => {
  const user = typeof roleOrUser === 'string' ? { role: roleOrUser } : roleOrUser || {};
  const role = user.role;

  if (role === 'admin') return '/admin';
  if (role === 'landlord') return '/landlord';
  if (role === 'manager') {
    if (user.accessMode !== 'restricted') return '/landlord';
    const capabilities = user.capabilities || [];
    if (capabilities.includes('portfolio') || capabilities.includes('leases_rent')) return '/landlord';
    return '/landlord/utilities';
  }
  return '/tenant';
};
